import { Injectable } from '@angular/core';
import { WizardPage } from '@app/workflow/components/navigator/wizard-view/interfaces';
import { QuestionStatusService } from '@app/pages/questions/question-status.service';
import { MessplatzService } from '@app/services/messplatz/messplatz.service';
import { SpecialQuestionService } from '@app/workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import { SessionItemData } from '@app/workflow/services/session-item-data';
import { IQuestionnaireGroup, IQuestionnaireItem, IQuestionnaireWorkflowItem } from '@interfaces/interfaces';
import { BlockQuestionItem, SpecialQuestionItem } from '@interfaces/questions';

@Injectable({
	providedIn: 'root',
})
export class DistractionFreeWizardService {

	constructor(
		private messplatzService: MessplatzService,
		private questionStatusService: QuestionStatusService,
		private specialQuestionService: SpecialQuestionService,
	) {}

	public processedWorkflow(workflowItem: IQuestionnaireWorkflowItem, item: SessionItemData): WizardPage[] {

		const pages: WizardPage[] = [];
		let originalGroupNumber = 0;
		//Fragegruppen hinzufügen
		for (const group of workflowItem.groups) {
			const allKeys = [];
			const firstPageInPagesIndex = pages.length;

			for (const preface of group.preface || []) {
				pages.push({
					...group,
					question: {
						type: 'prolog',
						title: group.title,
						description: preface,
					},
					obligatoryUnskippableKeys: [],
					obligatoryAllKindOfKeys: [],
				});
			}

			//Frageblöcke hinzufügen
			if (group.items.length === 0) {
				pages.push({
					...group,
					question: null,
					obligatoryUnskippableKeys: [],
					obligatoryAllKindOfKeys: [],
				});
			}
			for (const question of group.items) {
				let currentValue = null;
				let overwriteDescription = '';
				let hasPreface = false;

				for (const preface of question.preface || []) {
					if (question.type === 'BlockQuestion') {
						pages.push({
							...group,
							question: {
								type: 'prolog',
								title: question.title,
								description: preface,
							},
							obligatoryUnskippableKeys: [],
							obligatoryAllKindOfKeys: [],
						});
						hasPreface = true;
					} else {
						overwriteDescription += preface;
					}
				}
				// Informationen für Button auf der letzten Prolog-Seite berechnen
				if (hasPreface) {
					const groupStatus = this.questionStatusService.checkQuestionGroupStatus(group, item);
					pages[pages.length - 1].completed = groupStatus && groupStatus.status === 'finished';
				}

				if (question.type === 'BlockQuestion') {
					this.buildBlockQuestionGroups(question, group, pages, originalGroupNumber, allKeys);
				} else if (this.specialQuestionService.isSpecialQuestion(question.type)) {
					//Get Data
					const specialQuestion = question as SpecialQuestionItem;
					currentValue = item?.getProperty(specialQuestion.key);

					const specialQuestionImpl = this.specialQuestionService.getSpecialQuestion(specialQuestion);
					const completed = specialQuestionImpl.isCompleted(currentValue);
					const missingDevices = this.messplatzService?.getMissingDevicesNames(specialQuestionImpl.requiredDevices) || [];
					const hasRequiredHardware = !missingDevices.length;

					let nextButtonText = 'actions.starttest';
					if (completed) {
						nextButtonText = 'actions.restartTest';
					}
					if (!hasRequiredHardware && !completed) {
						nextButtonText = 'workflow.openManualInput';
					}
					else if (!hasRequiredHardware && completed) {
						nextButtonText = 'workflow.newOpenManualInput';
					}


					//Build Prolog
					const prolog = [];
					prolog.push({
						...group,
						question: {
							type: 'prolog',
							title: group.title,
							description: overwriteDescription || specialQuestionImpl.defaultGroupConfiguration.preface[0],
						},
						subpage: 0,
						originalPage: originalGroupNumber,
						obligatoryUnskippableKeys: [],
						obligatoryAllKindOfKeys: [],
						nextButtonOverwrite: nextButtonText,
						prologIndex: pages.length,
						epilogIndex: pages.length,
						isWizardSkipAllowed: completed,
						completed: completed,
						missingDevices: missingDevices,
						alignBottom: true,
					});

					//Build content
					const content: WizardPage[] = [];
					if (hasRequiredHardware) {
						const wizardPages = specialQuestionImpl.buildWizardPages(group, specialQuestion, originalGroupNumber, currentValue);
						content.push(...wizardPages);
					} else {
						//Alternative Eingabe Seiten
						const alternateInputGroup = [...specialQuestionImpl.defaultGroupConfiguration.items];
						for (const alternateInput of alternateInputGroup) {
							this.buildBlockQuestionGroups(alternateInput, group, content, originalGroupNumber, allKeys, specialQuestion.key);
						}
						content.forEach(c => c.missingDevices = missingDevices);
					}

					//Build Epilog
					const epilog = specialQuestionImpl.buildEpilogPages(group, specialQuestion);

					//Insert pages
					const prologIndex = pages.length;
					const epilogIndex = prologIndex + content.length + prolog.length;
					for (const page of [...prolog, ...content, ...epilog]) {
						page.prologIndex = prologIndex;
						page.epilogIndex = epilogIndex;
					}

					pages.push(...prolog);
					pages.push(...content);
					pages.push(...epilog);

					allKeys.push(specialQuestion.key);
				} else {
					// Fallback
					pages.push({
						...group,
						question: {
							type: 'prolog',
							title: group.title,
							description: `Test "${question.type}" ist nicht bekannt`,
						},
						obligatoryUnskippableKeys: [],
						obligatoryAllKindOfKeys: [],
					});
				}
			}

			for (const epilog of group.epilog || []) {
				pages.push({
					...group,
					question: {
						type: 'epilog',
						title: group.title,
						description: epilog,
					},
					obligatoryUnskippableKeys: [],
					obligatoryAllKindOfKeys: [],
					isProcedure: group.items.some(i => i.isProcedure),
				});
			}

			pages[firstPageInPagesIndex].allGroupKeys = allKeys;

			originalGroupNumber++;
		}
		return pages;
	}

	public buildBlockQuestionGroups(
		question: IQuestionnaireItem,
		group: IQuestionnaireGroup,
		pages: WizardPage[],
		originalPageNumber: number,
		allKeys: string[],
		parentKey: string = null) : void
	{
		if (question.type === 'BlockQuestion') {
			let obj:WizardPage;
			const blockQuestion = {...question as BlockQuestionItem};
			blockQuestion.items = [...blockQuestion.items.map(i => { return {...i};})];

			if (!blockQuestion.options?.wizard?.splitBlock) {
				obj = {
					...group,
					question: blockQuestion,
					obligatoryUnskippableKeys: [],
					obligatoryAllKindOfKeys: [],
					alignBottom: question.alignBottom,
					layout: blockQuestion.options?.wizard?.layout || 'horizontal',
				};
				for (const block of blockQuestion.items) {
					const key = parentKey ? parentKey + '.' + block.key : block.key;
					block.key = key;
					if (
						(blockQuestion.default?.isAnswerOptional && block.question?.isAnswerOptional === false) ||
						(!blockQuestion.default?.isAnswerOptional && block.question?.isAnswerOptional !== true)
					) {
						obj.obligatoryAllKindOfKeys.push(key);

						if (
							(blockQuestion.default?.isWizardSkipAllowed && block.question?.isWizardSkipAllowed === false) ||
							(!blockQuestion.default?.isWizardSkipAllowed && block.question?.isWizardSkipAllowed !== true)
						) {
							obj.obligatoryUnskippableKeys.push(key);
						}
					}
					allKeys.push(key);
				}
				pages.push(obj);
			} else {
				for (const block of blockQuestion.items) {
					const q: BlockQuestionItem = {
						...blockQuestion,
						items: [ block ]
					};
					const key = parentKey ? parentKey + '.' + block.key : block.key;
					block.key = key;
					obj = {
						...group,
						question: q,
						obligatoryUnskippableKeys: [],
						obligatoryAllKindOfKeys: [],
						alignBottom: blockQuestion.alignBottom,
						layout: blockQuestion.options?.wizard?.layout || 'horizontal',
					};
					if (!block.question?.isAnswerOptional) {
						obj.obligatoryAllKindOfKeys.push(key);
					}
					if (!block.question?.isAnswerOptional && !block.question?.isWizardSkipAllowed) {
						obj.obligatoryUnskippableKeys.push(key);
					}
					allKeys.push(key);
					pages.push(obj);
				}
			}
		}
	}
}
